import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authJsonHeaders } from '../utils/authHeaders';
import CabinetsAdminShell from './CabinetsAdminShell';
import ClientAddForm from './ClientAddForm';
import ClientEditForm from './ClientEditForm';
import './CabinetsSelectPage.css';
import './AdminCabinetsClientsPage.css';

function clientBelongsToCabinet(client, cabinet) {
  if (!cabinet) return true;
  const cid = client.cabinet_id != null ? Number(client.cabinet_id) : null;
  const cabId = Number(cabinet.id);
  if (Number(cabinet.is_default) === 1) {
    return cid == null || cid === 0 || cid === cabId;
  }
  return cid === cabId;
}

function resolveCabinetLabel(client, cabinets, defaultCab) {
  const cid = client.cabinet_id != null ? Number(client.cabinet_id) : null;
  if (cid == null || cid === 0) {
    return defaultCab?.name || 'Cabinet principal';
  }
  const found = cabinets.find((c) => Number(c.id) === cid);
  return found?.name || `Cabinet #${cid}`;
}

function resolveFromCabinetId(client, cabinets, defaultCab) {
  const cid = client.cabinet_id != null ? Number(client.cabinet_id) : null;
  if (cid == null || cid === 0) {
    return defaultCab?.id ?? null;
  }
  return cid;
}

export default function AdminCabinetsClientsPage({
  mode: modeProp,
  archivedOnly = false,
}) {
  const location = useLocation();
  const path = String(location.pathname || '');

  // mode explicite (actifs | inactifs) — inactifs = clients is_archived=1
  const listMode =
    modeProp === 'inactifs' ||
    archivedOnly ||
    path.includes('/clients/inactifs') ||
    path.includes('/clients/archives')
      ? 'inactifs'
      : 'actifs';
  const showArchived = listMode === 'inactifs';

  const { isAdmin } = useAuth();
  const [cabinets, setCabinets] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterCabinetId, setFilterCabinetId] = useState('');
  const [search, setSearch] = useState('');

  const [showAdd, setShowAdd] = useState(false);
  const [addCabinetId, setAddCabinetId] = useState('');
  const [editClient, setEditClient] = useState(null);
  const [deleteClient, setDeleteClient] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [archivingId, setArchivingId] = useState(null);
  const [unarchivingId, setUnarchivingId] = useState(null);
  const [transferClient, setTransferClient] = useState(null);
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferError, setTransferError] = useState('');

  const canManage = isAdmin();

  const defaultCabinet = useMemo(
    () => cabinets.find((c) => Number(c.is_default) === 1) || cabinets[0] || null,
    [cabinets]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Clients inactifs → archived=1 ; actifs → non archivés
      const clientsUrl = showArchived
        ? '/api/clients?all_cabinets=1&archived=1'
        : '/api/clients?all_cabinets=1&archived=0';
      const [cabRes, cliRes] = await Promise.all([
        fetch('/api/cabinets', {
          headers: authJsonHeaders({ 'X-Cabinet-Id': '' }),
        }),
        fetch(clientsUrl, {
          headers: authJsonHeaders({ 'X-Cabinet-Id': '' }),
        }),
      ]);
      const cabData = await cabRes.json();
      const cliData = await cliRes.json();
      if (!cabRes.ok || !cabData.success) {
        throw new Error(cabData.error || 'Impossible de charger les cabinets');
      }
      if (!cliRes.ok || !cliData.success) {
        throw new Error(cliData.error || cliData.message || 'Impossible de charger les clients');
      }
      setCabinets(Array.isArray(cabData.cabinets) ? cabData.cabinets : []);
      setClients(Array.isArray(cliData.clients) ? cliData.clients : []);
    } catch (err) {
      setError(err.message || 'Erreur de chargement');
      setCabinets([]);
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    if (canManage) loadData();
  }, [canManage, loadData, listMode]);

  useEffect(() => {
    setFilterCabinetId('');
    setSearch('');
    setClients([]);
  }, [listMode]);

  useEffect(() => {
    if (!showAdd || addCabinetId || !defaultCabinet) return;
    setAddCabinetId(String(defaultCabinet.id));
  }, [showAdd, addCabinetId, defaultCabinet]);

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filterCab = filterCabinetId
      ? cabinets.find((c) => String(c.id) === String(filterCabinetId))
      : null;

    const list = clients.filter((c) => {
      if (filterCab && !clientBelongsToCabinet(c, filterCab)) return false;
      if (!q) return true;
      const hay = [
        c.nom,
        c.prenom,
        c.telephone,
        c.email,
        c.num_chrono,
        c.username,
        c.matricule_fiscal,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });

    // Doublons d’anciens transferts (même nom + chrono archivés plusieurs fois)
    if (!showArchived) return list;
    const seen = new Set();
    return list.filter((c) => {
      const key = `${String(c.nom || '')
        .trim()
        .toLowerCase()}|${c.num_chrono != null ? String(c.num_chrono) : ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [clients, cabinets, filterCabinetId, search, showArchived]);

  const transferTargets = useMemo(() => {
    if (!transferClient) return [];
    const fromId = resolveFromCabinetId(transferClient, cabinets, defaultCabinet);
    return cabinets.filter((c) => Number(c.id) !== Number(fromId));
  }, [transferClient, cabinets, defaultCabinet]);

  const flashSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
  };

  const handleDelete = async () => {
    if (!deleteClient?.id || deleting) return;
    setDeleting(true);
    setError('');
    try {
      const res = await fetch(`/api/clients/${deleteClient.id}`, {
        method: 'DELETE',
        headers: authJsonHeaders({ 'X-Cabinet-Id': '' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.error || data.message || 'Suppression impossible');
      }
      setDeleteClient(null);
      flashSuccess(`Client « ${deleteClient.nom || ''} » supprimé`);
      await loadData();
    } catch (err) {
      setError(err.message || 'Erreur suppression');
      setDeleteClient(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleArchive = async (client) => {
    if (!client?.id || archivingId) return;
    const ok = window.confirm(
      `Archiver le client « ${client.nom || ''} » ?\nIl apparaîtra dans Clients inactifs.`
    );
    if (!ok) return;
    setArchivingId(client.id);
    setError('');
    try {
      const res = await fetch(`/api/clients/${client.id}/archive`, {
        method: 'PATCH',
        headers: authJsonHeaders(),
        body: JSON.stringify({ archived: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.error || data.message || 'Archivage impossible');
      }
      flashSuccess(`Client « ${client.nom || ''} » archivé`);
      await loadData();
    } catch (err) {
      setError(err.message || 'Erreur archivage');
    } finally {
      setArchivingId(null);
    }
  };

  const handleUnarchive = async (client) => {
    if (!client?.id || unarchivingId) return;
    setUnarchivingId(client.id);
    setError('');
    try {
      const res = await fetch(`/api/clients/${client.id}/archive`, {
        method: 'PATCH',
        headers: authJsonHeaders(),
        body: JSON.stringify({ archived: false }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.error || data.message || 'Désarchivage impossible');
      }
      flashSuccess(`Client « ${client.nom || ''} » réactivé`);
      await loadData();
    } catch (err) {
      setError(err.message || 'Erreur désarchivage');
    } finally {
      setUnarchivingId(null);
    }
  };

  const handleTransfer = async (toCabinet) => {
    if (!transferClient?.id || !toCabinet?.id || transferBusy) return;
    setTransferBusy(true);
    setTransferError('');
    try {
      const fromId = resolveFromCabinetId(transferClient, cabinets, defaultCabinet);
      const res = await fetch('/api/cabinets/transfer-client', {
        method: 'POST',
        headers: authJsonHeaders({ 'X-Cabinet-Id': '' }),
        body: JSON.stringify({
          client_id: transferClient.id,
          from_cabinet_id: fromId,
          to_cabinet_id: toCabinet.id,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Déplacement impossible');
      }
      const name = transferClient.nom || '';
      setTransferClient(null);
      flashSuccess(`« ${name} » déplacé vers ${toCabinet.name} avec tout son dossier`);
      await loadData();
    } catch (err) {
      setTransferError(err.message || 'Erreur lors du déplacement');
    } finally {
      setTransferBusy(false);
    }
  };

  if (!canManage) {
    return <Navigate to="/cabinets" replace />;
  }

  const statusWord = listMode === 'inactifs' ? 'inactif' : 'actif';
  const countLabel = `${filteredClients.length} client${filteredClients.length !== 1 ? 's' : ''} ${statusWord}${filteredClients.length !== 1 ? 's' : ''}`;

  const clientInitials = (c) => {
    const nom = String(c.nom || '').trim();
    const prenom = String(c.prenom || '').trim();
    if (prenom && nom) return `${prenom[0]}${nom[0]}`.toUpperCase();
    const parts = nom.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return (nom || '?').slice(0, 2).toUpperCase();
  };

  return (
    <CabinetsAdminShell>
      <div className={`ac-page${showArchived ? ' ac-page--inactive' : ''}`}>
        <header className="ac-hero">
          <div className="ac-hero__glow" aria-hidden="true" />
          <div className="ac-hero__text">
            <div className="ac-hero__eyebrow">
              {showArchived ? 'Clients inactifs' : 'Clients actifs'}
            </div>
            <h1>{showArchived ? 'Clients archivés' : 'Clients actifs'}</h1>
            <p>
              {showArchived
                ? 'Consultez et réactivez les clients archivés, ou déplacez-les entre cabinets.'
                : 'Gérez les clients actifs : recherche, déplacement entre cabinets, archivage.'}
            </p>
          </div>
          <div className="ac-hero__actions">
            {!showArchived ? (
              <button
                type="button"
                className="cabinets-btn cabinets-btn--primary"
                onClick={() => {
                  setAddCabinetId(defaultCabinet ? String(defaultCabinet.id) : '');
                  setShowAdd(true);
                }}
              >
                + Ajouter un client
              </button>
            ) : null}
          </div>
        </header>

        {error ? <div className="cabinets-alert">{error}</div> : null}
        {success ? <div className="cabinets-success">{success}</div> : null}

        <div className="ac-toolbar">
          <label className="ac-field">
            <span>Cabinet</span>
            <select
              value={filterCabinetId}
              onChange={(e) => setFilterCabinetId(e.target.value)}
            >
              <option value="">Tous les cabinets</option>
              {cabinets.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {Number(c.is_default) === 1 ? ' (données actuelles)' : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="ac-search">
            <span className="ac-search__icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
              </svg>
            </span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nom, téléphone, matricule…"
              aria-label="Recherche"
            />
          </label>

          <span className="ac-stat">{countLabel}</span>
        </div>

        {loading ? (
          <div className="cabinets-loading">Chargement des clients…</div>
        ) : filteredClients.length === 0 ? (
          <div className="cabinets-empty">
            {showArchived ? 'Aucun client archivé trouvé.' : 'Aucun client actif trouvé.'}
          </div>
        ) : (
          <div className="ac-list" role="list">
            {filteredClients.map((c) => {
              const nh = String(c.nh_type || '').toUpperCase() === 'NH';
              return (
                <article key={c.id} role="listitem" className="ac-card">
                  <div className="ac-card__main">
                    <div className={`ac-card__avatar${nh ? ' is-nh' : ''}`} aria-hidden="true">
                      {clientInitials(c)}
                    </div>
                    <div className="ac-card__info">
                      <strong>{c.nom || '—'}</strong>
                      {c.prenom ? <span className="ac-card__sub">{c.prenom}</span> : null}
                      <div className="ac-card__meta">
                        <span className="ac-pill ac-pill--cab">
                          {resolveCabinetLabel(c, cabinets, defaultCabinet)}
                        </span>
                        <span className={`ac-pill${nh ? ' ac-pill--nh' : ''}`}>
                          {c.nh_type || 'E'}
                        </span>
                        {c.num_chrono != null ? (
                          <span className="ac-pill ac-pill--muted">N° {c.num_chrono}</span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="ac-card__details">
                    <div>
                      <span className="ac-card__label">Téléphone</span>
                      <span>{c.telephone || '—'}</span>
                    </div>
                    <div>
                      <span className="ac-card__label">Matricule fiscal</span>
                      <span>{c.matricule_fiscal || '—'}</span>
                    </div>
                  </div>

                  <div className="ac-card__actions">
                    <button
                      type="button"
                      className="ac-act ac-act--move"
                      title="Déplacer vers un autre cabinet"
                      onClick={() => {
                        setTransferError('');
                        setTransferClient(c);
                      }}
                    >
                      Déplacer
                    </button>
                    <button
                      type="button"
                      className="ac-act ac-act--edit"
                      title="Modifier"
                      onClick={() => setEditClient(c)}
                    >
                      Modifier
                    </button>
                    {showArchived ? (
                      <button
                        type="button"
                        className="ac-act ac-act--restore"
                        title="Réactiver le client"
                        disabled={unarchivingId === c.id}
                        onClick={() => handleUnarchive(c)}
                      >
                        {unarchivingId === c.id ? '…' : 'Réactiver'}
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="ac-act ac-act--archive"
                          title="Archiver le client"
                          disabled={archivingId === c.id}
                          onClick={() => handleArchive(c)}
                        >
                          {archivingId === c.id ? '…' : 'Archiver'}
                        </button>
                        <button
                          type="button"
                          className="ac-act ac-act--del"
                          title="Supprimer"
                          onClick={() => setDeleteClient(c)}
                        >
                          Supprimer
                        </button>
                      </>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {showAdd && !showArchived ? (
        <div className="cabinets-modal-overlay" onClick={() => setShowAdd(false)}>
          <div
            className="cabinets-modal cabinets-modal--edit"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Nouveau client</h3>
            <p className="cabinets-modal-hint">
              Choisissez le cabinet de destination, puis renseignez la fiche client.
            </p>
            <label
              className="admin-clients-filter"
              style={{ marginBottom: '1rem', display: 'block' }}
            >
              <span>Cabinet</span>
              <select
                value={addCabinetId}
                onChange={(e) => setAddCabinetId(e.target.value)}
                required
              >
                {cabinets.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <ClientAddForm
              cabinetId={addCabinetId ? Number(addCabinetId) : null}
              onClientAdded={async () => {
                setShowAdd(false);
                flashSuccess('Client créé');
                await loadData();
              }}
              onCancel={() => setShowAdd(false)}
            />
          </div>
        </div>
      ) : null}

      {editClient ? (
        <div className="cabinets-modal-overlay" onClick={() => setEditClient(null)}>
          <div
            className="cabinets-modal cabinets-modal--edit"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Modifier le client</h3>
            <ClientEditForm
              client={editClient}
              userIsAdmin
              onClientUpdated={async () => {
                setEditClient(null);
                flashSuccess('Client mis à jour');
                await loadData();
              }}
              onCancel={() => setEditClient(null)}
            />
          </div>
        </div>
      ) : null}

      {deleteClient ? (
        <div
          className="cabinets-modal-overlay"
          onClick={() => !deleting && setDeleteClient(null)}
        >
          <div className="cabinets-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Supprimer le client</h3>
            <p className="cabinets-modal-hint">
              Confirmer la suppression définitive de{' '}
              <strong>{deleteClient.nom || 'ce client'}</strong> ? Cette action est
              irréversible.
            </p>
            <div className="cabinets-modal-actions">
              <button
                type="button"
                className="cabinets-btn cabinets-btn--ghost"
                onClick={() => setDeleteClient(null)}
                disabled={deleting}
              >
                Annuler
              </button>
              <button
                type="button"
                className="cabinets-btn cabinets-btn--danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Suppression…' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {transferClient ? (
        <div
          className="cabinets-modal-overlay"
          onClick={() => !transferBusy && setTransferClient(null)}
        >
          <div className="cabinets-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Déplacer le client</h3>
            <p className="cabinets-modal-hint">
              Déplacer <strong>{transferClient.nom || 'ce client'}</strong> vers un autre
              cabinet. Tout le dossier suit : charges, opérations, factures, tickets et
              historique.
            </p>
            {transferError ? <div className="cabinets-alert">{transferError}</div> : null}
            {transferTargets.length === 0 ? (
              <p className="cabinets-modal-hint">Aucun autre cabinet disponible.</p>
            ) : (
              <div className="cabinets-pick-list">
                {transferTargets.map((cab) => (
                  <button
                    key={cab.id}
                    type="button"
                    className="cabinets-pick-item"
                    disabled={transferBusy}
                    onClick={() => handleTransfer(cab)}
                  >
                    <strong>{cab.name}</strong>
                    <span>{cab.code}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="cabinets-modal-actions">
              <button
                type="button"
                className="cabinets-btn cabinets-btn--ghost"
                onClick={() => setTransferClient(null)}
                disabled={transferBusy}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </CabinetsAdminShell>
  );
}
